import { ChevronDownIcon, ChevronRightIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import DiffStatusSticker from 'dashboard/apps/InquiryTemplateEditor/DiffStatusSticker';
import { EMPTY_LABEL } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummary/InquiryTemplateVersionDiffSummary';
import { VersionDiffChangeType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import { Cell, Row } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const DiffTagLabelContainer = styled.span`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
`;

const TableRowContentContainer = styled.div`
  padding-left: 20px;
`;

const TableRowContentHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledLabel = styled(Label)<{ primary?: boolean }>`
  color: ${({ theme, primary }) =>
    primary ? theme.color.text.primary : theme.color.text.secondary};
  cursor: pointer;
`;

const LabelWithDiffTag = ({
  value,
  changeType,
}: {
  value: string;
  changeType: VersionDiffChangeType | undefined;
}) => {
  return (
    <DiffTagLabelContainer>
      {value}
      {changeType && <DiffStatusSticker changeType={changeType} />}
    </DiffTagLabelContainer>
  );
};

export interface TableRowWithDiffTagProps {
  label: string;
  changeType: VersionDiffChangeType | undefined;
  baseNode?: React.ReactNode;
  targetNode?: React.ReactNode;
}

const TableRowWithDiffTag = ({
  label,
  changeType,
  baseNode,
  targetNode,
}: TableRowWithDiffTagProps) => {
  const childProvided = baseNode || targetNode;
  const [isOpen, { toggle }] = useToggle(true);

  return (
    <Row>
      <Cell>
        <TableRowContentHeader onClick={toggle}>
          {childProvided && (
            <>
              {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
              <Spacer width={0.5} height={0} />
            </>
          )}

          <div style={{ flexGrow: 1 }}>
            {changeType === VersionDiffChangeType.REMOVED ? (
              <LabelWithDiffTag value={label} changeType={changeType} />
            ) : (
              <StyledLabel primary={VersionDiffChangeType.MODIFIED === changeType}>
                {label}
              </StyledLabel>
            )}
          </div>
        </TableRowContentHeader>
        <TableRowContentContainer>
          {childProvided && isOpen && (baseNode || <>{EMPTY_LABEL}</>)}
        </TableRowContentContainer>
      </Cell>
      <Cell>
        <TableRowContentHeader onClick={toggle}>
          {childProvided && (
            <>
              {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
              <Spacer width={0.5} height={0} />
            </>
          )}

          <div style={{ flexGrow: 1 }}>
            {!(changeType == null) &&
            [VersionDiffChangeType.ADDED, VersionDiffChangeType.MODIFIED].includes(changeType) ? (
              <LabelWithDiffTag value={label} changeType={changeType} />
            ) : (
              <StyledLabel>{label}</StyledLabel>
            )}
          </div>
        </TableRowContentHeader>
        <TableRowContentContainer>
          {childProvided && isOpen && (targetNode || <>{EMPTY_LABEL}</>)}
        </TableRowContentContainer>
      </Cell>
    </Row>
  );
};

export default TableRowWithDiffTag;
